import { Chip, useTheme } from "@mui/material";
import type { CSSProperties, JSX } from "react";

import { EnhancedTooltip } from "../enhancedTooltip";

type ChipType = "success" | "warning" | "error" | "primary" | "default" | "disabled";

interface CustomChipProps {
  label: string;
  tooltip?: {
    header?: string;
    content?: string;
  };
  chipType: ChipType;
  icon?: JSX.Element;
  fontSize?: CSSProperties["fontSize"];
  sx?: CSSProperties;
  "data-cy"?: string;
}

export const CustomChip = ({
  label,
  tooltip,
  chipType,
  icon,
  fontSize,
  sx,
  "data-cy": dataCy,
}: CustomChipProps): JSX.Element => {
  const theme = useTheme();

  const colors = {
    success: {
      color: theme.palette.success.dark,
      backgroundColor: theme.palette.success.light,
      borderColor: theme.palette.success.dark,
    },
    warning: {
      color: theme.palette.warning.dark,
      backgroundColor: theme.palette.warning.light,
      borderColor: theme.palette.warning.dark,
    },
    error: {
      color: theme.palette.error.dark,
      backgroundColor: theme.palette.error.light,
      borderColor: theme.palette.error.dark,
    },
    primary: {
      color: theme.palette.primary.main,
      backgroundColor: theme.semanticColors.surface.blueBg,
      borderColor: theme.palette.primary.main,
    },
    default: {
      color: theme.semanticColors.text.tertiary,
      backgroundColor: theme.semanticColors.background.subtle,
      borderColor: theme.semanticColors.text.tertiary,
    },
    disabled: {
      color: theme.semanticColors.text.disabled,
      backgroundColor: theme.semanticColors.background.subtle,
      borderColor: theme.semanticColors.text.disabled,
    },
  };

  const chipContent = (
    <Chip
      sx={{
        ...colors[chipType],
        fontWeight: 600,
        fontSize: fontSize ?? "12px",
        padding: "4px 10px",
        cursor: tooltip ? "pointer" : "default",
        "& .MuiChip-icon": {
          color: colors[chipType].color,
        },
        ...sx,
      }}
      variant="outlined"
      label={label}
      size="small"
      icon={icon}
      data-cy={dataCy}
    />
  );

  return tooltip ? (
    <EnhancedTooltip content={tooltip.content} header={tooltip.header}>
      {chipContent}
    </EnhancedTooltip>
  ) : (
    chipContent
  );
};
